import { useEffect, useState, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LayoutDashboard, MessageSquareWarning, CalendarCheck, Building2, TriangleAlert, Users, KeyRound, Mail, Send, Power, Trash2, CreditCard, Check, UserPlus, Bell, Utensils, Wine, BedDouble, Wrench, PartyPopper, Hotel } from "lucide-react";
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import Shell from "@/components/Shell";
import { Panel, Btn, Field, Input, Select, StatusBadge, AllergyFlag } from "@/components/kit";
import { GuestCreate } from "@/components/GuestCreate";
import { NotificationsPanel } from "@/components/NotificationsPanel";
import { ServicesManager } from "@/components/ServicesManager";
import api, { formatApiError } from "@/lib/api";

const NAV = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "notifications", label: "Dashboard", icon: Bell },
  { key: "complaints", label: "Complaints", icon: MessageSquareWarning },
  { key: "bookings", label: "Bookings", icon: CalendarCheck },
  { key: "escalations", label: "Escalations", icon: TriangleAlert },
  { key: "services", label: "Services", icon: Utensils },
  { key: "team", label: "Team", icon: Users },
  { key: "guests", label: "Guests", icon: UserPlus },
  { key: "billing", label: "Billing", icon: CreditCard },
  { key: "setup", label: "Setup", icon: Building2 },
  { key: "/reception", label: "Reception Portal", icon: Hotel },
  { key: "/kitchen", label: "Kitchen Portal", icon: Utensils },
  { key: "/food-beverage", label: "F&B Portal", icon: Wine },
  { key: "/housekeeping", label: "Housekeeping Portal", icon: BedDouble },
  { key: "/maintenance", label: "Maintenance Portal", icon: Wrench },
  { key: "/events", label: "Events Portal", icon: PartyPopper },
];

const COLORS = ["#FF5E36", "#FF2A6D", "#A855F7", "#00D2FF", "#FFB020", "#34D399"];

export default function AdminPortal() {
  const navigate = useNavigate();
  const [active, setActive] = useState("overview");
  const [data, setData] = useState({ analytics: null, complaints: [], bookings: [], departments: [], locations: [], staff: [], escalations: [] });
  const load = () => {
    Promise.all([
      api.get("/analytics"), api.get("/complaints"), api.get("/bookings"),
      api.get("/departments"), api.get("/locations"), api.get("/staff"), api.get("/escalations"),
    ]).then(([a, c, b, d, l, s, e]) =>
      setData({ analytics: a.data, complaints: c.data, bookings: b.data, departments: d.data, locations: l.data, staff: s.data, escalations: e.data })
    ).catch(() => {});
  };
  useEffect(load, []);

  return (
    <Shell title="Management Console" subtitle="Cross-location oversight & operational insight" nav={NAV} active={active} onNav={(k) => (k.startsWith("/") ? navigate(k) : setActive(k))}>
      {active === "overview" && <Overview a={data.analytics} />}
      {active === "notifications" && <NotificationsPanel title="Everything across BeetDrop" intro="Live activity from every portal — new requests, assignments, approvals, handovers and escalations across all departments." />}
      {active === "complaints" && <Complaints complaints={data.complaints} departments={data.departments} staff={data.staff} reload={load} />}
      {active === "bookings" && <Bookings bookings={data.bookings} />}
      {active === "escalations" && <Escalations items={data.escalations} reload={load} />}
      {active === "services" && <ServicesManager />}
      {active === "team" && <Team staff={data.staff} locations={data.locations} reload={load} />}
      {active === "guests" && <GuestCreate />}
      {active === "billing" && <Billing />}
      {active === "setup" && <Setup data={data} reload={load} />}
    </Shell>
  );
}

function Stat({ label, value, sub, accent }) {
  return (
    <Panel className="p-5 relative overflow-hidden">
      <div className={`absolute -right-6 -top-6 w-20 h-20 rounded-full blur-2xl opacity-40 ${accent}`} />
      <div className="text-xs font-semibold uppercase tracking-wider text-purple-300/60">{label}</div>
      <div className="font-head text-3xl font-extrabold text-white mt-1">{value}</div>
      {sub && <div className="text-xs text-purple-400 mt-1">{sub}</div>}
    </Panel>
  );
}

function Overview({ a }) {
  if (!a) return <Panel className="p-12 text-center text-purple-400/70">Loading analytics…</Panel>;
  const t = a.totals;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Pending complaints" value={t.pending_complaints} sub={`${t.resolved_complaints} resolved`} accent="bg-amber-500" />
        <Stat label="Resolved complaints" value={t.resolved_complaints} sub={`of ${t.complaints} total`} accent="bg-emerald-500" />
        <Stat label="Approved bookings" value={t.approved_bookings} sub={`${t.rejected_bookings} rejected`} accent="bg-sky-500" />
        <Stat label="Avg resolution" value={`${a.avg_resolution_hours}h`} sub={`response ${a.avg_response_hours}h`} accent="bg-[#FF2A6D]" />
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <Panel className="p-6">
          <h3 className="font-head font-bold text-white mb-4">Complaints by status</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={a.complaint_status}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2E1256" />
              <XAxis dataKey="status" stroke="#8C7DAE" fontSize={12} />
              <YAxis stroke="#8C7DAE" fontSize={12} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#180735", border: "1px solid #451B7F", borderRadius: 12, color: "#fff" }} />
              <Bar dataKey="count" radius={[6, 6, 0, 0]}>{a.complaint_status.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Bar>
            </BarChart>
          </ResponsiveContainer>
        </Panel>
        <Panel className="p-6">
          <h3 className="font-head font-bold text-white mb-4">Most common complaint types</h3>
          {a.common_complaints.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={a.common_complaints} dataKey="count" nameKey="category" cx="50%" cy="50%" outerRadius={90} label={(e) => e.category}>
                  {a.common_complaints.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "#180735", border: "1px solid #451B7F", borderRadius: 12, color: "#fff" }} />
              </PieChart>
            </ResponsiveContainer>
          ) : <p className="text-purple-400 text-sm">No data yet.</p>}
        </Panel>
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <Panel className="p-6">
          <h3 className="font-head font-bold text-white mb-4">Bookings: approved vs rejected</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={[{ name: "Approved", v: t.approved_bookings }, { name: "Rejected", v: t.rejected_bookings }]}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2E1256" />
              <XAxis dataKey="name" stroke="#8C7DAE" fontSize={12} /><YAxis stroke="#8C7DAE" fontSize={12} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#180735", border: "1px solid #451B7F", borderRadius: 12, color: "#fff" }} />
              <Bar dataKey="v" radius={[6, 6, 0, 0]}><Cell fill="#34D399" /><Cell fill="#FF2A6D" /></Bar>
            </BarChart>
          </ResponsiveContainer>
        </Panel>
        <Panel className="p-6 flex flex-col justify-center gap-4">
          <h3 className="font-head font-bold text-white">Response & resolution times</h3>
          <div><div className="text-xs uppercase tracking-wider text-purple-300/60">Avg first response</div><div className="font-head text-2xl font-bold text-[#00D2FF]">{a.avg_response_hours} hours</div></div>
          <div><div className="text-xs uppercase tracking-wider text-purple-300/60">Avg resolution</div><div className="font-head text-2xl font-bold text-[#FF6B7D]">{a.avg_resolution_hours} hours</div></div>
        </Panel>
      </div>
    </div>
  );
}

function Complaints({ complaints, departments, staff, reload }) {
  const [sel, setSel] = useState({});
  const assign = async (c) => {
    const s = sel[c.id] || {};
    if (!s.department_id) return toast.error("Pick a department");
    try { await api.patch(`/complaints/${c.id}/assign`, { department_id: s.department_id, staff_id: s.staff_id || null }); toast.success("Assigned"); reload(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const unassigned = complaints.filter((c) => !c.department_id);
  return (
    <div className="space-y-4">
      {unassigned.length > 0 && <div className="text-sm text-amber-300 font-semibold">{unassigned.length} unassigned complaint(s) need attention</div>}
      {complaints.map((c) => (
        <Panel key={c.id} className="p-5" data-testid={`acomp-${c.id}`}>
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2"><h3 className="font-head font-bold text-white">{c.subject}</h3><code className="text-[11px] text-amber-300">{c.reference}</code></div>
              <p className="text-xs text-purple-400 mt-0.5">{c.location_name} · {c.category} · {c.guest_name} · {new Date(c.created_at).toLocaleDateString()}</p>
            </div>
            <StatusBadge status={c.status} />
          </div>
          <p className="text-sm text-purple-200/80 mt-2">{c.description}</p>
          {c.department_name && <p className="text-xs text-purple-300 mt-2">Routed to {c.department_name}{c.assigned_to_name ? ` · ${c.assigned_to_name}` : ""}</p>}
          {c.resolution_note && <p className="text-xs text-emerald-300 mt-2">Resolution: {c.resolution_note}</p>}
          {c.status !== "resolved" && (
            <div className="flex flex-wrap items-end gap-2 mt-4">
              <div className="min-w-[150px]"><Select data-testid={`a-dept-${c.id}`} value={sel[c.id]?.department_id || ""} onChange={(e) => setSel({ ...sel, [c.id]: { ...sel[c.id], department_id: e.target.value } })}><option value="">Department…</option>{departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}</Select></div>
              <div className="min-w-[150px]"><Select data-testid={`a-staff-${c.id}`} value={sel[c.id]?.staff_id || ""} onChange={(e) => setSel({ ...sel, [c.id]: { ...sel[c.id], staff_id: e.target.value } })}><option value="">Staff…</option>{staff.filter((s) => s.department_id === sel[c.id]?.department_id).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</Select></div>
              <Btn data-testid={`a-assign-${c.id}`} onClick={() => assign(c)}>Assign</Btn>
            </div>
          )}
        </Panel>
      ))}
    </div>
  );
}

function Bookings({ bookings }) {
  if (!bookings.length) return <Panel className="p-12 text-center text-purple-400/70">No bookings.</Panel>;
  return (
    <div className="overflow-x-auto rounded-xl border border-[#2E1256] bg-[#14052E]">
      <table className="w-full text-sm">
        <thead><tr className="text-left text-xs uppercase tracking-wider text-purple-300 bg-[#1F0842]">{["Guest", "Service", "When", "Party", "Dept", "Allergy", "Status"].map((h) => <th key={h} className="p-3">{h}</th>)}</tr></thead>
        <tbody>{bookings.map((b) => (
          <tr key={b.id} className="border-t border-[#230948] hover:bg-[#1E0A3E]">
            <td className="p-3 text-purple-100">{b.guest_name}</td><td className="p-3 text-purple-100">{b.service_name}</td>
            <td className="p-3 text-purple-300">{b.date} {b.time}</td><td className="p-3 text-purple-300">{b.party_size}</td>
            <td className="p-3 text-purple-300">{b.department_name}</td>
            <td className="p-3">{b.allergy_info ? <AllergyFlag info={b.allergy_info} /> : "—"}</td><td className="p-3"><StatusBadge status={b.status} /></td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

function Escalations({ items, reload }) {
  const resolve = async (e) => { try { await api.patch(`/escalations/${e.id}/resolve`); toast.success("Resolved"); reload(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };
  if (!items.length) return <Panel className="p-12 text-center text-purple-400/70">No escalations.</Panel>;
  return (
    <div className="space-y-4">{items.map((e) => (
      <Panel key={e.id} className="p-5" data-testid={`aesc-${e.id}`}>
        <div className="flex items-start justify-between"><div><h3 className="font-head font-bold text-white">{e.title}</h3><p className="text-xs text-rose-300 uppercase font-semibold mt-0.5">{e.severity} · {e.department_name} · {e.author}</p></div><StatusBadge status={e.status} /></div>
        <p className="text-sm text-purple-200/80 mt-2">{e.detail}</p>
        {e.status === "open" && <Btn variant="secondary" className="mt-3" data-testid={`aesc-resolve-${e.id}`} onClick={() => resolve(e)}>Mark resolved</Btn>}
      </Panel>
    ))}</div>
  );
}

const ROLE_LABEL = { admin: "Owner / GM", manager: "Dept Manager", reception: "Reception", staff: "Staff" };

function Team({ staff, locations, reload }) {
  const [resetId, setResetId] = useState(null);
  const [pw, setPw] = useState("");
  const locName = (id) => locations.find((l) => l.id === id)?.name || "—";
  const team = [...staff].sort((a, b) => (a.role === "admin" ? -1 : b.role === "admin" ? 1 : 0));
  const doReset = async (s) => {
    if (pw.length < 6) return toast.error("Password must be at least 6 characters");
    try {
      const r = await api.post(`/staff/${s.id}/reset-password`, { password: pw });
      toast.success(`Password reset for ${r.data.name}`); setResetId(null); setPw("");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const sendLink = async (s) => {
    try { const r = await api.post(`/staff/${s.id}/send-reset`); toast.success(`Reset link emailed to ${r.data.name}`); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const toggleActive = async (s) => {
    const next = s.active === false;
    try { await api.patch(`/staff/${s.id}/status`, { active: next }); toast.success(`${s.name} ${next ? "reactivated" : "deactivated"}`); reload && reload(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const removeStaff = async (s) => {
    if (!window.confirm(`Remove ${s.name}? This permanently deletes their account.`)) return;
    try { await api.delete(`/staff/${s.id}`); toast.success(`${s.name} removed`); reload && reload(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  if (!team.length) return <Panel className="p-12 text-center text-purple-400/70">No team members yet. Add staff in the Setup tab.</Panel>;
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-sm text-purple-300"><Users size={16} /> {team.length} team member{team.length !== 1 ? "s" : ""} in your business</div>
      <div className="overflow-x-auto rounded-xl border border-[#2E1256] bg-[#14052E]">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs uppercase tracking-wider text-purple-300 bg-[#1F0842]">
            {["Name", "Email", "Role", "Department", "Location", "Action"].map((h) => <th key={h} className="p-3">{h}</th>)}
          </tr></thead>
          <tbody>
            {team.map((s) => (
              <Fragment key={s.id}>
                <tr className="border-t border-[#230948] hover:bg-[#1E0A3E]" data-testid={`team-row-${s.id}`}>
                  <td className="p-3 text-purple-100 font-medium">{s.name}{s.active === false && <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-rose-500/15 text-rose-300 border border-rose-500/30">Inactive</span>}{s.title && <span className="block text-[11px] text-purple-400 font-normal">{s.title}</span>}</td>
                  <td className="p-3 text-purple-300"><span className="inline-flex items-center gap-1.5"><Mail size={12} /> {s.email}</span></td>
                  <td className="p-3"><span className="text-[11px] px-2 py-1 rounded-full bg-purple-500/15 text-purple-200 border border-purple-500/30">{ROLE_LABEL[s.role] || s.role}</span></td>
                  <td className="p-3 text-purple-300">{s.department_name || "—"}</td>
                  <td className="p-3 text-purple-300">{locName(s.location_id)}</td>
                  <td className="p-3">
                    {s.role !== "admin" ? (
                      <div className="flex flex-wrap gap-1.5">
                        <Btn variant="outline" data-testid={`reset-${s.id}`} onClick={() => { setResetId(resetId === s.id ? null : s.id); setPw(""); }}><KeyRound size={13} /> Set password</Btn>
                        <Btn variant="outline" data-testid={`sendlink-${s.id}`} onClick={() => sendLink(s)}><Send size={13} /> Email reset link</Btn>
                        <Btn variant="outline" data-testid={`toggle-${s.id}`} onClick={() => toggleActive(s)}><Power size={13} /> {s.active === false ? "Reactivate" : "Deactivate"}</Btn>
                        <Btn variant="danger" data-testid={`remove-${s.id}`} onClick={() => removeStaff(s)}><Trash2 size={13} /> Remove</Btn>
                      </div>
                    ) : <span className="text-xs text-purple-500">Owner</span>}
                  </td>
                </tr>
                {resetId === s.id && (
                  <tr className="bg-[#1A0736]"><td colSpan={6} className="p-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="w-full sm:w-72"><Input data-testid={`reset-input-${s.id}`} type="text" placeholder="New password (min 6 characters)" value={pw} onChange={(e) => setPw(e.target.value)} /></div>
                      <Btn data-testid={`reset-save-${s.id}`} onClick={() => doReset(s)}>Save new password</Btn>
                      <Btn variant="ghost" onClick={() => { setResetId(null); setPw(""); }}>Cancel</Btn>
                    </div>
                  </td></tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Billing() {
  const [biz, setBiz] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState("");
  const [cycle, setCycle] = useState("monthly");
  useEffect(() => {
    api.get("/business/me").then((r) => setBiz(r.data)).catch(() => {});
    api.get("/subscription/config").then((r) => setCfg(r.data)).catch(() => {});
  }, []);

  const pay = async (planId) => {
    setBusy(planId);
    try {
      const callback_url = `${window.location.origin}/payment/callback`;
      const r = await api.post("/subscription/initialize", { plan: planId, billing_cycle: cycle, callback_url });
      window.location.href = r.data.authorization_url;
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setBusy(""); }
  };

  if (!biz || !cfg) return <Panel className="p-12 text-center text-purple-400/70">Loading billing…</Panel>;
  const cur = cfg.currency === "GBP" ? "£" : cfg.currency === "USD" ? "$" : cfg.currency === "NGN" ? "₦" : `${cfg.currency} `;
  const statusLabel = { active: "Active", trialing: "Free trial", past_due: "Past due", canceled: "Canceled" }[biz.subscription_status] || biz.subscription_status;
  return (
    <div className="space-y-6" data-testid="billing-panel">
      {cfg.dummy && (
        <Panel className="p-4 border-amber-500/30 bg-amber-500/10 text-amber-100 text-sm" data-testid="billing-dummy-banner">
          Test mode — payments are simulated. No real charge is made until live Paystack keys are configured.
        </Panel>
      )}
      <Panel className="p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-wider text-purple-300/60">Current plan</div>
            <div className="font-head text-2xl font-extrabold text-white mt-1 capitalize">{biz.plan}</div>
          </div>
          <div className="text-right">
            <div className="text-xs uppercase tracking-wider text-purple-300/60">Status</div>
            <div className="mt-1"><StatusBadge status={biz.subscription_status} label={statusLabel} /></div>
          </div>
        </div>
        {biz.trial_ends_at && biz.subscription_status === "trialing" && (
          <p className="text-xs text-purple-300 mt-3">Free trial ends {new Date(biz.trial_ends_at).toLocaleDateString()}.</p>
        )}
        {biz.last_payment_at && <p className="text-xs text-emerald-300 mt-3">Last payment {new Date(biz.last_payment_at).toLocaleDateString()} · ref {biz.last_payment_reference}</p>}
      </Panel>

      <div className="flex items-center gap-2">
        <span className="text-sm text-purple-300">Billing cycle:</span>
        <div className="inline-flex rounded-xl border border-[#2E1256] overflow-hidden">
          {["monthly", "annual"].map((c) => (
            <button key={c} data-testid={`cycle-${c}`} onClick={() => setCycle(c)}
              className={`px-4 py-1.5 text-sm font-semibold capitalize transition-colors ${cycle === c ? "bg-[#FF2A6D] text-white" : "text-purple-300 hover:text-white"}`}>
              {c}{c === "annual" ? " · 2 months free" : ""}
            </button>
          ))}
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        {cfg.plans.map((p) => {
          const amount = cycle === "annual" ? p.amount * 10 : p.amount;
          const isCurrent = biz.plan === p.id && biz.subscription_status === "active";
          return (
            <Panel key={p.id} className="p-6 flex flex-col" data-testid={`plan-card-${p.id}`}>
              <div className="font-head text-lg font-bold text-white">{p.name}</div>
              <div className="font-head text-3xl font-extrabold text-white mt-2">{cur}{amount}<span className="text-sm font-normal text-purple-400">/{cycle === "annual" ? "year" : "month"}</span></div>
              <div className="flex-1" />
              {isCurrent ? (
                <div className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-300"><Check size={15} /> Current plan</div>
              ) : (
                <Btn className="mt-5 w-full" disabled={busy === p.id} data-testid={`pay-${p.id}`} onClick={() => pay(p.id)}>
                  {busy === p.id ? "Redirecting…" : `Pay ${cur}${amount}`}
                </Btn>
              )}
            </Panel>
          );
        })}
      </div>
    </div>
  );
}

function Setup({ data, reload }) {
  const [loc, setLoc] = useState({ name: "", city: "", address: "" });
  const [dept, setDept] = useState({ name: "", location_id: "", is_custom: true });
  const [staff, setStaff] = useState({ name: "", email: "", password: "", location_id: "", department_id: "", role: "staff", title: "" });

  const addLoc = async (e) => { e.preventDefault(); try { await api.post("/locations", loc); toast.success("Location added"); setLoc({ name: "", city: "", address: "" }); reload(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };
  const addDept = async (e) => { e.preventDefault(); try { await api.post("/departments", dept); toast.success("Department added"); setDept({ name: "", location_id: "", is_custom: true }); reload(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };
  const addStaff = async (e) => { e.preventDefault(); try { await api.post("/staff", staff); toast.success("Staff account created"); setStaff({ ...staff, name: "", email: "", password: "", title: "" }); reload(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };

  const sDepts = data.departments.filter((d) => d.location_id === staff.location_id);
  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <Panel className="p-6">
        <h3 className="font-head font-bold text-white mb-4">Add location</h3>
        <form onSubmit={addLoc} className="space-y-3">
          <Field label="Name"><Input data-testid="loc-name" required value={loc.name} onChange={(e) => setLoc({ ...loc, name: e.target.value })} /></Field>
          <Field label="City"><Input data-testid="loc-city" value={loc.city} onChange={(e) => setLoc({ ...loc, city: e.target.value })} /></Field>
          <Field label="Address"><Input data-testid="loc-addr" value={loc.address} onChange={(e) => setLoc({ ...loc, address: e.target.value })} /></Field>
          <Btn type="submit" data-testid="loc-add">Add location</Btn>
        </form>
        <div className="mt-4 space-y-2">{data.locations.map((l) => <div key={l.id} className="text-sm text-purple-300">{l.name} · {l.city}</div>)}</div>
      </Panel>

      <Panel className="p-6">
        <h3 className="font-head font-bold text-white mb-4">Add department</h3>
        <form onSubmit={addDept} className="space-y-3">
          <Field label="Location"><Select data-testid="dept-loc" required value={dept.location_id} onChange={(e) => setDept({ ...dept, location_id: e.target.value })}><option value="">Select…</option>{data.locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}</Select></Field>
          <Field label="Name" hint="Default or custom (e.g. Spa, Pool, Security)"><Input data-testid="dept-name" required value={dept.name} onChange={(e) => setDept({ ...dept, name: e.target.value })} /></Field>
          <Btn type="submit" data-testid="dept-add">Add department</Btn>
        </form>
      </Panel>

      <Panel className="p-6">
        <h3 className="font-head font-bold text-white mb-4">Create staff account</h3>
        <form onSubmit={addStaff} className="space-y-3">
          <Field label="Name"><Input data-testid="staff-name" required value={staff.name} onChange={(e) => setStaff({ ...staff, name: e.target.value })} /></Field>
          <Field label="Email"><Input data-testid="staff-email" type="email" required value={staff.email} onChange={(e) => setStaff({ ...staff, email: e.target.value })} /></Field>
          <Field label="Password"><Input data-testid="staff-pass" required value={staff.password} onChange={(e) => setStaff({ ...staff, password: e.target.value })} /></Field>
          <Field label="Location"><Select data-testid="staff-loc" required value={staff.location_id} onChange={(e) => setStaff({ ...staff, location_id: e.target.value, department_id: "" })}><option value="">Select…</option>{data.locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}</Select></Field>
          <Field label="Department"><Select data-testid="staff-dept" required value={staff.department_id} onChange={(e) => setStaff({ ...staff, department_id: e.target.value })}><option value="">Select…</option>{sDepts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}</Select></Field>
          <Field label="Role"><Select data-testid="staff-role" value={staff.role} onChange={(e) => setStaff({ ...staff, role: e.target.value })}><option value="staff">Staff</option><option value="reception">Reception</option></Select></Field>
          <Field label="Title"><Input data-testid="staff-title" value={staff.title} onChange={(e) => setStaff({ ...staff, title: e.target.value })} /></Field>
          <Btn type="submit" data-testid="staff-add">Create account</Btn>
        </form>
      </Panel>
    </div>
  );
}
